import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { FIRM } from "../data/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/knowledge-centre", label: "Knowledge Centre" },
  { to: "/contact", label: "Contact" },
];

export function Logo({ dark = false }) {
  return (
    <Link to="/" data-testid="logo-link" className="leading-none">
      <span className={`block font-serif text-lg sm:text-xl font-bold tracking-wide ${dark ? "text-white" : "text-navy"}`}>
        NEERAJ BATTAN <span className="text-gold">&amp;</span> ASSOCIATES
      </span>
      <span className={`block text-[10px] sm:text-[11px] font-mono uppercase tracking-[0.35em] mt-1 ${dark ? "text-slate-400" : "text-slate-500"}`}>
        Chartered Accountants
      </span>
    </Link>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/92 backdrop-blur-md border-b border-slate-200/70 shadow-[0_8px_30px_-18px_rgba(11,25,44,0.35)]" : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Logo />
          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium tracking-wide uppercase transition-colors ${
                    isActive ? "text-gold" : "text-slate-600 hover:text-navy"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={`tel:${FIRM.phone1Raw}`}
              data-testid="nav-call-button"
              className="flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors"
            >
              <Phone size={15} strokeWidth={2.2} />
              {FIRM.phone1}
            </a>
            <Link
              to="/contact"
              data-testid="nav-book-consultation-button"
              className="ml-2 rounded-full bg-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-navy-light transition-colors"
            >
              Book a Consultation
            </Link>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-navy"
            aria-label="Toggle navigation"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-4 py-6 space-y-1" data-testid="mobile-nav">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `block px-3 py-3 rounded-lg text-base font-medium ${isActive ? "text-gold bg-slate-50" : "text-slate-700"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="mobile-book-consultation-button"
            className="block mt-4 text-center rounded-full bg-navy px-5 py-3.5 text-sm font-semibold text-white"
          >
            Book a Consultation
          </Link>
        </div>
      )}
    </header>
  );
}
